import { EAutonomousCommunity, UnsubscribableEmailType } from '@constants';
import { IExamGroupNotification, IUserSettings } from '@sdk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { sdk } from '@/services';

const UserSettingsService = sdk.getService('UserSettingsService');

export const useMutateUserSettings = (userId: string) => {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['user-settings', userId] });
    queryClient.invalidateQueries({ queryKey: ['user-settings'] });
    queryClient.invalidateQueries({ queryKey: ['profile'] });
  };

  const cancelAll = async () => {
    await Promise.all([
      queryClient.cancelQueries({ queryKey: ['user-settings', userId] }),
      queryClient.cancelQueries({ queryKey: ['user-settings'] })
    ]);
  };

  const readSnapshots = () => ({
    previousScoped: queryClient.getQueryData<IUserSettings>(['user-settings', userId]),
    previousGlobal: queryClient.getQueryData<IUserSettings>(['user-settings'])
  });

  const writeBoth = (next: IUserSettings) => {
    queryClient.setQueryData(['user-settings', userId], next);
    queryClient.setQueryData(['user-settings'], next);
  };

  const restoreSnapshots = (ctx?: {
    previousScoped?: IUserSettings;
    previousGlobal?: IUserSettings;
  }) => {
    if (ctx?.previousScoped)
      queryClient.setQueryData(['user-settings', userId], ctx.previousScoped);
    if (ctx?.previousGlobal) queryClient.setQueryData(['user-settings'], ctx.previousGlobal);
  };

  const { mutate: setAllowEmailNotifications, isPending: isUpdatingAllow } = useMutation({
    mutationFn: (map: Partial<Record<UnsubscribableEmailType, boolean>>) =>
      UserSettingsService.updateAllowEmailNotifications(userId, map),
    onMutate: async map => {
      await cancelAll();
      const { previousScoped, previousGlobal } = readSnapshots();
      const base = previousScoped || previousGlobal;
      if (base) {
        const next: IUserSettings = {
          ...base,
          notificationSettings: {
            ...base.notificationSettings,
            allowEmailNotifications: {
              ...(base.notificationSettings?.allowEmailNotifications || {}),
              ...map
            }
          }
        };
        writeBoth(next);
      }
      return { previousScoped, previousGlobal };
    },
    onError: (_err, _vars, ctx) => restoreSnapshots(ctx),
    onSuccess: invalidateAll
  });

  const { mutate: setExamGroupNotificationsMaster, isPending: isUpdatingMaster } = useMutation({
    mutationFn: (isActive: boolean) => UserSettingsService.updateExamGroupMaster(userId, isActive),
    onMutate: async nextIsActive => {
      await cancelAll();
      const { previousScoped, previousGlobal } = readSnapshots();
      const base = previousScoped || previousGlobal;
      if (base) {
        const next: IUserSettings = {
          ...base,
          notificationSettings: {
            ...base.notificationSettings,
            examGroupNotificationSettings: {
              ...base.notificationSettings.examGroupNotificationSettings,
              isActive: nextIsActive
            }
          }
        };
        if (!nextIsActive) {
          next.notificationSettings.examGroupNotificationSettings.examGroupNotifications = [];
        }
        writeBoth(next);
      }
      return { previousScoped, previousGlobal };
    },
    onError: (_err, _vars, ctx) => restoreSnapshots(ctx),
    onSuccess: invalidateAll
  });

  const { mutate: setExamGroupNotifications, isPending: isUpdatingList } = useMutation({
    mutationFn: (examGroupNotifications: IExamGroupNotification[]) =>
      UserSettingsService.updateExamGroupNotifications(userId, examGroupNotifications),
    onMutate: async list => {
      await cancelAll();
      const { previousScoped, previousGlobal } = readSnapshots();
      const base = previousScoped || previousGlobal;
      if (base) {
        const next: IUserSettings = {
          ...base,
          notificationSettings: {
            ...base.notificationSettings,
            examGroupNotificationSettings: {
              ...base.notificationSettings.examGroupNotificationSettings,
              examGroupNotifications: list.map(({ tag, autonomousCommunities }) => ({
                tag,
                autonomousCommunities: (autonomousCommunities || []) as EAutonomousCommunity[]
              }))
            }
          }
        };
        writeBoth(next);
      }
      return { previousScoped, previousGlobal };
    },
    onError: (_err, _vars, ctx) => restoreSnapshots(ctx),
    onSuccess: invalidateAll
  });

  return {
    setAllowEmailNotifications,
    setExamGroupNotificationsMaster,
    setExamGroupNotifications,
    isWorking: isUpdatingAllow || isUpdatingMaster || isUpdatingList
  };
};
